import React, { useState, useEffect } from 'react';
import { Card, Select, Button, Form, message, Table, Tag, Space, DatePicker, Popconfirm, Typography } from 'antd';
import { LinkOutlined, DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import moment from 'moment';

const { Text } = Typography;

const Pairing = () => {
    const [form] = Form.useForm();
    const [mentors, setMentors] = useState([]);
    const [mentees, setMentees] = useState([]);
    const [pairings, setPairings] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchData = async () => {
        setLoading(true);
    try {
        const [usersRes, pairingRes] = await Promise.all([
            api.get('/admin/users-list'), // Use the new dedicated list endpoint
            api.get('/admin/pairings')
        ]);
        
        // No more filtering on the frontend! 
        // The backend gives us clean lists for the dropdowns.
        setMentors(usersRes.data.mentors);
        setMentees(usersRes.data.mentees);
        
        // Handle if pairings is paginated or a raw array
        const pairingData = Array.isArray(pairingRes.data) 
            ? pairingRes.data 
            : pairingRes.data.data;
            
        setPairings(pairingData);
    } catch (error) {
        console.error(error);
        message.error('Failed to load pairing data. Check console for details.');
    } finally {
        setLoading(false);
    }
};

    useEffect(() => { fetchData(); }, []);

    const onFinish = async (values) => {
        try {
            const payload = {
                ...values,
                start_date: values.start_date.format('YYYY-MM-DD')
            };
            await api.post('/admin/pair', payload);
            message.success('Mentor and Mentee paired successfully!');
            form.resetFields();
            fetchData(); // Refresh list
        } catch (error) {
            message.error(error.response?.data?.message || 'Pairing failed');
        }
    };

    const deletePairing = async (id) => {
        try {
            await api.delete(`/admin/pairings/${id}`);
            message.success('Pairing removed');
            fetchData();
        } catch (e) {
            message.error('Failed to delete pairing');
        }
    };

    // Table Columns Configuration
    const columns = [
        {
            title: 'Mentor',
            key: 'mentor',
            render: (_, record) => (
                <div>
                    <Text strong>{record.mentor?.name}</Text>
                    <br />
                    <Tag color="blue">{record.mentor?.profession}</Tag>
                </div>
            )
        },
        {
            title: 'Mentee',
            key: 'mentee',
            render: (_, record) => <Text>{record.mentee?.name}</Text>
        },
        {
            title: 'Start Date',
            dataIndex: 'start_date',
            render: (date) => moment(date).format('LL')
        },
        {
            title: 'Status',
            dataIndex: 'status',
            render: (status) => (
                <Tag color={status === 'active' ? 'green' : 'orange'}>
                    {status.toUpperCase()}
                </Tag>
            )
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, record) => (
                <Popconfirm 
                    title="Remove this pairing?" 
                    onConfirm={() => deletePairing(record.id)}
                    okText="Yes" 
                    cancelText="No"
                >
                    <Button type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
            )
        }
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Creation Form */}
            <Card title={<span><LinkOutlined /> Create New Pairing</span>}>
                <Form form={form} layout="vertical" onFinish={onFinish} style={{ display: 'flex', gap: '16px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <Form.Item name="mentor_id" label="Select Mentor" rules={[{ required: true }]} style={{ width: 250 }}>
                        <Select 
                            showSearch 
                            placeholder="Search Mentor" 
                            optionFilterProp="label" // Changed to label for easier searching
                        >
                            {mentors.map(m => (
                                <Select.Option key={m.id} value={m.id} label={m.name}>
                                    <Text strong>{m.name} ({m.profession})</Text>
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="mentee_id" label="Select Mentee" rules={[{ required: true }]} style={{ width: 250 }}>
                        <Select showSearch placeholder="Search Mentee" optionFilterProp="children">
                            {mentees.map(m => <Select.Option key={m.id} value={m.id}>{m.name}</Select.Option>)}
                        </Select>
                    </Form.Item>

                    <Form.Item name="start_date" label="Start Date" rules={[{ required: true }]}>
                        <DatePicker />
                    </Form.Item>

                    <Form.Item>
                        <Button type="primary" htmlType="submit" icon={<LinkOutlined />} style={{ backgroundColor: '#FE1F08', borderColor: '#FE1F08' }}>
                            Pair Users
                        </Button>
                    </Form.Item>
                </Form>
            </Card>

            {/* Existing Pairings Table */}
            <Card title={<span><TeamOutlined /> Active Mentorship Pairings</span>}>
                <Table 
                    dataSource={pairings} 
                    columns={columns} 
                    rowKey="id" 
                    loading={loading}
                    pagination={{ pageSize: 5 }}
                    scroll={{ x: 'max-content' }}
                />
            </Card>
        </div>
    );
};

export default Pairing;